import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { CheckCircle2, XCircle, ChevronLeft, ChevronRight, Mail, Loader2, RefreshCw } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

/**
 * E-Mail-Verlauf eines Mitglieds.
 * Loader = Funktion, die ({skip, limit}) → Promise<{items, total, skip, limit}> zurückgibt.
 * showError = darf die Fehlermeldung angezeigt werden? (Admin: ja, Self-Service: nein)
 * canResend = Resend-Button anzeigen (nur Admin)
 */
const PAGE_SIZE = 10;

const TYPE_LABELS = {
    newsletter: 'Newsletter',
    confirmation: 'Buchungsbestätigung',
    payment_request: 'Zahlungsanforderung',
    payment_confirmation: 'Zahlungsbestätigung',
    meeting_link: 'Online-Zugang',
    license: 'Lizenz',
    license_renewed: 'Lizenz verlängert',
    license_renewal_admin_notification: 'Lizenz-Verlängerung (Admin)',
    license_renewal_approval: 'Lizenz-Verlängerung (Genehmigung)',
    license_renewal_rejected: 'Lizenz-Verlängerung (Ablehnung)',
    license_reminder: 'Lizenz-Ablauf-Erinnerung',
    certificate: 'Seminarbescheinigung',
    welcome: 'Willkommen',
    email_change_notification: 'E-Mail-Änderung',
    password_reset: 'Passwort zurücksetzen',
    training_record_approved: 'Fortbildung anerkannt',
    training_record_ue_modified: 'Fortbildung teilweise anerkannt',
    training_record_rejected: 'Fortbildung abgelehnt',
    participant_bulk: 'Teilnehmer-Mail',
    participant_info: 'Teilnehmer-Info',
    participant_thanks: 'Teilnehmer-Dank',
    custom: 'Mail',
};

// Welche Mail-Typen kann das Backend per /email-logs/{id}/resend erneut versenden?
const RESEND_SUPPORTED = new Set([
    'confirmation',
    'payment_request',
    'payment_confirmation',
    'meeting_link',
]);

export function MemberMailHistory({
    loader,
    showError = false,
    canResend = false,
    title = 'E-Mail-Verlauf',
    testIdPrefix = 'member-mail',
}) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    const [loading, setLoading] = useState(false);
    const [resendingId, setResendingId] = useState(null);

    const load = useCallback(async (nextSkip = 0) => {
        setLoading(true);
        try {
            const r = await loader({ skip: nextSkip, limit: PAGE_SIZE });
            setItems(r.data?.items || []);
            setTotal(r.data?.total || 0);
            setSkip(r.data?.skip ?? nextSkip);
        } catch {
            setItems([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [loader]);

    useEffect(() => { load(0); }, [load]);

    const handleResend = async (log) => {
        if (!RESEND_SUPPORTED.has(log.template_type)) return;
        const label = TYPE_LABELS[log.template_type] || log.template_type;
        if (!window.confirm(`„${label}" erneut an ${log.recipient_email} senden?`)) return;
        setResendingId(log.id);
        try {
            await api.post(`/email-logs/${log.id}/resend`);
            toast.success('E-Mail erneut versendet');
            load(skip);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erneuter Versand fehlgeschlagen');
        } finally {
            setResendingId(null);
        }
    };

    if (!loading && total === 0) {
        return (
            <div className="rounded-lg border bg-white p-6" data-testid={`${testIdPrefix}-history`}>
                <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                    <Mail className="h-5 w-5 text-muted-foreground" /> {title}
                </h2>
                <p className="text-sm text-muted-foreground italic">Noch keine versendeten E-Mails.</p>
            </div>
        );
    }

    return (
        <div className="rounded-lg border bg-white p-6" data-testid={`${testIdPrefix}-history`}>
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Mail className="h-5 w-5 text-muted-foreground" /> {title}
                <span className="text-xs font-normal text-muted-foreground">
                    {total} {total === 1 ? 'E-Mail' : 'E-Mails'}
                </span>
                <Button variant="ghost" size="sm" className="ml-auto" onClick={() => load(skip)} disabled={loading} title="Aktualisieren">
                    <Loader2 className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                </Button>
            </h2>
            <ul className="flex flex-col divide-y">
                {items.map(log => {
                    const dt = new Date(log.sent_at);
                    const dateLabel = isNaN(dt.getTime()) ? log.sent_at : dt.toLocaleString('de-DE', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                    });
                    const ok = log.status === 'sent';
                    const typeLabel = TYPE_LABELS[log.template_type] || log.template_type || 'Mail';
                    const canResendThis = canResend && ok && RESEND_SUPPORTED.has(log.template_type);
                    return (
                        <li key={log.id} className="py-3 flex items-start gap-4" data-testid={`${testIdPrefix}-row-${log.id}`}>
                            {/* Status-Icon */}
                            <div className={`shrink-0 mt-1 ${ok ? 'text-emerald-600' : 'text-red-500'}`} title={showError && log.error ? log.error : (ok ? 'Versendet' : 'Fehler')}>
                                {ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                            </div>
                            {/* Inhalt */}
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                    <p className="text-sm font-medium text-gray-900 break-words flex-1 min-w-[12rem]">{log.subject}</p>
                                    <span className="text-xs text-muted-foreground whitespace-nowrap">{dateLabel}</span>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                    <span
                                        className="inline-block bg-gray-100 text-gray-700 text-[11px] px-2 py-0.5 rounded-full font-medium"
                                        title={log.template_type ? `template_type: ${log.template_type}` : undefined}
                                        data-testid={`${testIdPrefix}-type-${log.id}`}
                                    >
                                        {typeLabel}
                                    </span>
                                    {log.template_name && (
                                        <span
                                            className="text-[11px] text-muted-foreground italic truncate max-w-[18rem]"
                                            title={`Verwendete Vorlage: ${log.template_name}`}
                                            data-testid={`${testIdPrefix}-tplname-${log.id}`}
                                        >
                                            · {log.template_name}
                                        </span>
                                    )}
                                    {log.recipient_email && (
                                        <span className="text-xs text-muted-foreground truncate" title={log.recipient_email}>
                                            an {log.recipient_email}
                                        </span>
                                    )}
                                    {!ok && (
                                        <span className="text-xs text-red-600">Fehler</span>
                                    )}
                                </div>
                            </div>
                            {/* Resend */}
                            {canResendThis && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="shrink-0 text-gray-600 hover:text-primary hover:bg-primary/10"
                                    onClick={() => handleResend(log)}
                                    disabled={resendingId === log.id}
                                    title="Erneut senden"
                                    data-testid={`${testIdPrefix}-resend-${log.id}`}
                                >
                                    {resendingId === log.id
                                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        : <RefreshCw className="h-3.5 w-3.5" />}
                                </Button>
                            )}
                        </li>
                    );
                })}
            </ul>
            {total > PAGE_SIZE && (
                <div className="flex items-center justify-between gap-2 pt-3 mt-2 border-t" data-testid={`${testIdPrefix}-pagination`}>
                    <p className="text-xs text-muted-foreground">
                        {skip + 1}–{Math.min(skip + items.length, total)} von {total}
                    </p>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="outline" size="sm"
                            onClick={() => load(Math.max(0, skip - PAGE_SIZE))}
                            disabled={skip === 0 || loading}
                            data-testid={`${testIdPrefix}-prev`}
                        >
                            <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Zurück
                        </Button>
                        <Button
                            variant="outline" size="sm"
                            onClick={() => load(skip + PAGE_SIZE)}
                            disabled={skip + PAGE_SIZE >= total || loading}
                            data-testid={`${testIdPrefix}-next`}
                        >
                            Weiter <ChevronRight className="h-3.5 w-3.5 ml-1" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
